import { Stack, Typography } from '@mui/material';
import { PaymentComponent } from 'components';
import PopupConfirm, { TYPE_ICON } from 'components/popup/PopupConfirm';
import { STORAGE_KEY } from 'constants/global';
import { SYS_MESS } from 'constants/systemMessage';
import {
  PAYMENT_METHOD,
  PAYMENT_STATUS,
  PAYMENT_TYPE,
  PAY_JOB_PAYLOAD,
} from 'models/payments';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  setOpenPaymentSuccess,
  updateStatusPayNow,
} from 'store/reducers/payments';
import {
  checkoutACJobAction,
  checkoutExtendJobAction,
  checkoutTrialJobAction,
  payExtendJobByTopUpAction,
  payForExtendJobAction,
  payForExtendJobWithZeroAmountAction,
  payForExtendTrialJobAction,
  payForNormalJobAction,
  payForTrialJobAction,
  payJobByTopUpAction,
} from 'store/reducers/payments/actionTypes';
import { formatPrice } from 'utils';

export const PaymentJobs = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { openPaymentSuccess, paymentMethodId } = useAppSelector(
    (state) => state.payment,
  );
  const { dataCheckoutPayment } = useAppSelector((state) => state.payment);
  const { balance, total_amount, is_can_purchase_package, amount } =
    dataCheckoutPayment;

  const [searchParams] = useSearchParams();

  const jobId = searchParams.get('jobId');
  const isJobTrial = searchParams.get('isTrial') === 'true';
  const isJobExtend = searchParams.get('isExtend') === 'true';

  const handleBackAction = () => {
    navigate(-1);
  };

  const checkoutJobAction = (isMethodPayNow: boolean) => {
    if (!jobId) {
      handleBackAction();
    }

    const dispatchAction = isJobExtend
      ? checkoutExtendJobAction
      : isJobTrial
      ? checkoutTrialJobAction
      : checkoutACJobAction;

    dispatch(
      dispatchAction({
        data: {
          payment_method_type:
            isJobExtend || isJobTrial
              ? isMethodPayNow
                ? PAYMENT_METHOD.PAY_NOW
                : PAYMENT_METHOD.CARD
              : undefined,
          job_id: jobId,
        },
        onFailed: () => {
          handleBackAction();
        },
      }),
    );
  };

  const getPaymentAction = () => {
    if (isJobTrial) {
      return isJobExtend
        ? total_amount === 0
          ? payForExtendJobWithZeroAmountAction
          : payForExtendTrialJobAction
        : payForTrialJobAction;
    }
    if (balance < total_amount) {
      return is_can_purchase_package
        ? isJobExtend
          ? payForExtendJobAction
          : payForNormalJobAction
        : isJobExtend
        ? payExtendJobByTopUpAction
        : payJobByTopUpAction;
    }
    return isJobExtend ? payForExtendJobAction : payForNormalJobAction;
  };

  const paymentCallback = (isMethodPayNow: boolean, callback?: Function) => {
    const isNotEnoughBalance =
      balance < total_amount && !is_can_purchase_package;
    const shouldAddMoreParams =
      total_amount !== 0 && (isJobTrial || isNotEnoughBalance);

    const params = {
      job_id: jobId,
      ...(shouldAddMoreParams && {
        payment_method_type: isMethodPayNow
          ? PAYMENT_METHOD.PAY_NOW
          : PAYMENT_METHOD.CARD,
        ...(!isMethodPayNow && { payment_method_id: paymentMethodId }),
      }),
    };

    dispatch(
      getPaymentAction()({
        data: params as PAY_JOB_PAYLOAD,
        onSuccess: (qrCode: string) => {
          if (
            isMethodPayNow &&
            total_amount !== 0 &&
            (isJobTrial || isNotEnoughBalance)
          ) {
            callback?.(qrCode);
            return;
          }
          dispatch(setOpenPaymentSuccess(true));
          dispatch(updateStatusPayNow(PAYMENT_STATUS.SUCCESS));
        },
        onFailed: () => {
          dispatch(updateStatusPayNow(PAYMENT_STATUS.FAILED));
        },
        onFinally: () => {},
      }),
    );
  };

  return (
    <Stack spacing={8}>
      <Stack spacing={1} alignItems="center">
        <Typography variant="Web_Title_22">Payment</Typography>
        <Typography variant="Web_Label_18">
          {isJobExtend
            ? 'Accept and pay for booking'
            : isJobTrial
            ? 'Book a Trial'
            : 'Buy a Plan'}
        </Typography>
      </Stack>
      <React.Fragment>
        <PaymentComponent
          basicFees={
            <Stack>
              <Typography variant="Components_Column_content" color={'#6d6d6d'}>
                {isJobTrial ? 'Trial Fees' : 'Job Fee'}
              </Typography>
              <Typography variant="Components_Column_content" color={'#6d6d6d'}>
                {formatPrice(amount)}
              </Typography>
            </Stack>
          }
          qrCodeKey={
            isJobTrial
              ? STORAGE_KEY.QR_CODE_TRIAL_JOB
              : balance < total_amount && !is_can_purchase_package
              ? STORAGE_KEY.QR_CODE_TOP_UP
              : undefined
          }
          paymentType={
            isJobTrial
              ? PAYMENT_TYPE.TRIAL_JOB
              : balance >= total_amount
              ? PAYMENT_TYPE.AC_JOB_ENOUGH_BALANCE
              : PAYMENT_TYPE.AC_JOB_NOT_ENOUGH_BALANCE
          }
          checkoutAction={checkoutJobAction}
          handleBackAction={handleBackAction}
          paymentCallback={paymentCallback}
        />
      </React.Fragment>
      {openPaymentSuccess && (
        <PopupConfirm
          typeIcon={TYPE_ICON.CHECK}
          title={SYS_MESS.SUCCESS.PAYMENT}
          content={
            <Typography variant="Web_Label_14">
              Your payment has been successfully made
            </Typography>
          }
          textButton="Back to Bookings"
          onSubmit={handleBackAction}
          handleBack={() => navigate(-1)}
          handleClose={() => {
            dispatch(setOpenPaymentSuccess(false));
          }}
          justCloseWithBtn
        />
      )}
    </Stack>
  );
};

export default PaymentJobs;
